import React, { useState, useRef, useEffect } from 'react';

const EMAIL_DOMAINS = [
  'inbox.lv',
  'gmail.com',
  'icloud.com',
  'tvnet.lv',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'mail.lv',
  'delfi.lv',
  'email.lv',
];

interface EmailInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  error?: string;
  required?: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const EmailInput: React.FC<EmailInputProps> = ({
  label,
  error,
  required = false,
  value,
  onChange,
  onBlur,
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  const id = props.id || props.name || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;

  // Compute suggestions based on current value
  const getSuggestions = (): string[] => {
    if (!value || value.length === 0) return [];

    const atIndex = value.indexOf('@');

    if (atIndex === -1) {
      // No @ yet — show all domains appended to the typed text
      return EMAIL_DOMAINS.map(domain => `${value}@${domain}`);
    }

    const localPart = value.substring(0, atIndex);
    const domainPart = value.substring(atIndex + 1);

    if (!localPart) return [];

    // Filter domains that start with what the user typed after @
    const filtered = EMAIL_DOMAINS.filter(domain =>
      domain.toLowerCase().startsWith(domainPart.toLowerCase())
    );

    // If the typed domain exactly matches one domain, don't show suggestions
    if (filtered.length === 1 && filtered[0].toLowerCase() === domainPart.toLowerCase()) {
      return [];
    }

    return filtered.map(domain => `${localPart}@${domain}`);
  };

  const suggestions = getSuggestions();

  const handleFocus = () => {
    if (value.length > 0) {
      setShowSuggestions(true);
    }
    setActiveSuggestionIndex(-1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    setShowSuggestions(true);
    setActiveSuggestionIndex(-1);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Delay hiding so clicks on suggestions register first
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
      }
    }, 150);
    onBlur?.(e);
  };

  const selectSuggestion = (suggestion: string) => {
    // Create a synthetic event to match the expected onChange signature
    const syntheticEvent = {
      target: {
        name: props.name || '',
        value: suggestion,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  // Scroll active suggestion into view
  useEffect(() => {
    if (activeSuggestionIndex >= 0 && suggestionsRef.current) {
      const activeItem = suggestionsRef.current.children[activeSuggestionIndex] as HTMLElement;
      activeItem?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeSuggestionIndex]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const visibleSuggestions = showSuggestions && suggestions.length > 0;

  return (
    <div className="space-y-2 mt-6 relative" ref={containerRef}>
      <label
        htmlFor={id}
        className="block text-base font-semibold text-gray-700 mb-0.5"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="email"
          autoComplete="off"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`
            w-full px-2.5 h-10 border rounded-10 text-base
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            transition-colors duration-200
            ${error
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
            }
            bg-white
          `}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-required={required}
          aria-autocomplete="list"
          aria-expanded={visibleSuggestions}
          aria-controls={visibleSuggestions ? `${id}-suggestions` : undefined}
          aria-activedescendant={
            visibleSuggestions && activeSuggestionIndex >= 0
              ? `${id}-suggestion-${activeSuggestionIndex}`
              : undefined
          }
          role="combobox"
          {...props}
        />
      </div>

      {visibleSuggestions && (
        <ul
          id={`${id}-suggestions`}
          ref={suggestionsRef}
          role="listbox"
          className="
            absolute z-50 left-0 right-0 mt-1
            bg-white border border-gray-200 rounded-lg shadow-lg
            max-h-64 overflow-y-auto text-left
          "
        >
          {suggestions.map((suggestion, index) => {
            const atIndex = suggestion.indexOf('@');
            const localPart = suggestion.substring(0, atIndex);
            const domainPart = suggestion.substring(atIndex);

            return (
              <li
                key={suggestion}
                id={`${id}-suggestion-${index}`}
                role="option"
                aria-selected={index === activeSuggestionIndex}
                className={`
                  px-4 py-2.5 cursor-pointer text-sm
                  ${index === activeSuggestionIndex
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50'
                  }
                  transition-colors duration-100
                `}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(suggestion);
                }}
                onMouseEnter={() => setActiveSuggestionIndex(index)}
              >
                <span className="font-medium">{localPart}</span>
                <span className="text-gray-500">{domainPart}</span>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p
          id={`${id}-error`}
          className="text-sm text-red-500"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default EmailInput;
